import type * as C from "@zooi/contracts";
import { INBOX_LABELS as I, PLAN_REVIEW_LABELS as R } from "./labels.ts";
import type { CheckStatus } from "./ui/review.tsx";

// The full-page plan review (docs/design-system.md §0.4d, plan M7 step 7f),
// kept apart from the components so the rules are tested.

export interface OutlineEntry { id: string; label: string; group?: boolean; card?: boolean }

/** The outline: summary and checks, then a card per campaign and per action, then what was not chosen. */
export function planOutline(plan: Pick<C.AgentPlan, "body">): OutlineEntry[] {
  const { campaigns, actions, not_chosen } = plan.body;
  return [
    { id: "summary", label: R.summary },
    { id: "checks", label: R.checks },
    ...(campaigns.length ? [{ id: "group-campaigns", label: I.campaigns, group: true }, ...campaigns.map((campaign) => ({ id: `campaign-${campaign.ref}`, label: campaign.title, card: true }))] : []),
    ...(actions.length ? [{ id: "group-actions", label: I.actions, group: true }, ...actions.map((action) => ({ id: `action-${action.ref}`, label: action.title, card: true }))] : []),
    ...(not_chosen.length ? [{ id: "not-chosen", label: I.notChosen }] : []),
  ];
}

/**
 * What the server checked when the plan arrived, as recorded on it: the
 * person's capacity, and for each action its authorship tier against the
 * limit and whether a pre-authorization covers it. Nothing the server did
 * not record is shown, least of all as passed.
 */
export function planChecks(plan: Pick<C.AgentPlan, "checked" | "body">): { status: CheckStatus; label: string; detail?: string }[] {
  const capacity = plan.checked.capacity;
  const out: { status: CheckStatus; label: string; detail?: string }[] = [{
    status: capacity.requested_minutes <= capacity.remaining_minutes ? "pass" : "fail",
    label: R.capacityCheck,
    detail: I.capacity(capacity.requested_minutes, capacity.remaining_minutes, capacity.weekly_minutes),
  }];
  for (const action of plan.body.actions) {
    const entry = plan.checked.authorship.find((check) => check.ref === action.ref);
    if (!entry) continue;
    const withinTier = entry.declared === entry.limit || entry.declared === "person_writes";
    out.push({
      status: !withinTier ? "fail" : entry.grant_id || entry.default_grant ? "pass" : "attention",
      label: action.title,
      detail: `${I.authorship[entry.declared] ?? entry.declared} · ${entry.grant_id ? I.covered : entry.default_grant ? I.coveredDefault : I.notCovered}`,
    });
  }
  return out;
}

export interface PlanTotals {
  campaigns: number;
  actions: number;
  requestedMinutes: number;
  remainingMinutes: number;
  /** The most the actions may spend together; null when no action declares spending. */
  spendUsd: number | null;
  passed: number;
  checks: number;
}

/**
 * The plan in one look (design system §0.3e, §0.4d): what it asks for, of the
 * person's time and money, and how the server's checks came out, before any
 * of the agent's text.
 */
export function planTotals(plan: Pick<C.AgentPlan, "checked" | "body">): PlanTotals {
  const checks = planChecks(plan);
  const spending = plan.body.actions.filter((action) => action.spend);
  return {
    campaigns: plan.body.campaigns.length,
    actions: plan.body.actions.length,
    requestedMinutes: plan.checked.capacity.requested_minutes,
    remainingMinutes: plan.checked.capacity.remaining_minutes,
    spendUsd: spending.length ? spending.reduce((sum, action) => sum + (action.spend?.amount_usd ?? 0), 0) : null,
    passed: checks.filter((check) => check.status === "pass").length,
    checks: checks.length,
  };
}
